import React, {useContext, useState, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {useLocation} from 'react-router'

import {useBusinessLinesByCustomerId} from '../../Roles'
import {AppState} from '../../Root.store'
import {selectSelectedCustomerFilter} from '../Customers/Customers.selector'
import {useFeaturesState} from '../Features'

export interface RMCOrderIntakeContextInterface {
  isFetching: boolean
  hasRMCBusinessLine: boolean
  isFeatureEnabled: boolean
}

const RMCOrderIntakeContext = React.createContext<RMCOrderIntakeContextInterface>({
  isFetching: false,
  hasRMCBusinessLine: false,
  isFeatureEnabled: false
})

RMCOrderIntakeContext.displayName = 'RMCOrderIntake'

export const useRMCOrderIntake = () => useContext(RMCOrderIntakeContext)

export const RMCOrderIntakeProvider = ({children}: {children: React.ReactNode}) => {
  const {pathname} = useLocation()
  const isOrderIntake =
    pathname.startsWith('/rmcDelivery') ||
    pathname.startsWith('/overview') ||
    pathname.startsWith('/orders')
  if (!isOrderIntake) return <>{children}</>
  return <RMCOrderIntakeBase>{children}</RMCOrderIntakeBase>
}

const RMCOrderIntakeBase = ({children}) => {
  const [hasRMCBusinessLine, setHasRMCBusinessLine] = useState<boolean>(false)
  const customerId = useSelector<AppState, string | undefined>(
    (state) => selectSelectedCustomerFilter(state)?.customerId
  )

  // Check if the RMC feature toggle is enabled
  const {getFeature} = useFeaturesState()
  const isFeatureEnabled = getFeature('OrderIntakeRMCHub')

  // We only need the business lines to determine if customer has RMC service
  const {data: businessLines} = useBusinessLinesByCustomerId(customerId)

  // Simple loading state for UI purposes
  const [isFetching, setIsFetching] = useState(false)

  // Update hasRMCBusinessLine based on the business lines in the customer's roles
  useEffect(() => {
    // Start loading
    setIsFetching(true)

    if (!customerId || !businessLines) {
      setHasRMCBusinessLine(false)
      setIsFetching(false)
      return
    }

    // Check if 'RMC' exists in the business lines array
    const hasRMC = businessLines.includes('RMC')
    setHasRMCBusinessLine(hasRMC)

    // Loading finished
    setIsFetching(false)
  }, [customerId, businessLines])

  return (
    <RMCOrderIntakeContext.Provider
      value={{
        isFetching,
        hasRMCBusinessLine,
        isFeatureEnabled
      }}
    >
      {children}
    </RMCOrderIntakeContext.Provider>
  )
}
